export default {
  // Toolbar
  toolbar: {
    searchPlaceholder: "Search samples",
    refreshTooltip: "Refresh samples",
    filterTooltip: "Filter samples",

    tabs: {
      samples: "Samples",
      visits: "Visits",
    },
  },

  // Sample list
  samples: {
    title: "Samples",
    noSamples: "There are currently no samples available.",
    noSamplesSubtitle: "Samples will appear here when they are ready for validation.",
    refreshSamples: "Refresh",
    noResults: 'No samples found for "{query}"',
    noSamplesMatchFilters: "No samples match the filters",
    clearFilters: "Edit Filters",
    errorLoading: "An error occurred while loading samples.",
    error404: "API endpoint not found (404). Please check your configuration.",
    error500: "Server error (500). Please try again later.",
    errorAuth: "Authentication error. Please log in again.",
    errorNetwork: "Network error. Please check your internet connection.",
    retry: "Retry",

    actions: {
      highPriority: "High Priority",
      more: "More Actions",
      edit: {
        title: "Edit Sample Barcode",
        description: "Change the barcode for this sample.",
      },
      info: {
        title: "Sample Information",
        description: "View sample details",
      },
      rejectAll: {
        title: "Reject All Results",
        description: "Reject all pending results for this sample.",
      },
    },
  },

  // Patient info
  patient: {
    dateOfBirth: "Date of birth",
    male: "M",
    female: "F",
  },

  // Visit info
  visit: {
    tooltips: {
      visitId: "Visit ID",
      visitDate: "Date of visit",
      visitCategory: "Visit category",
      visitLocation: "Visit location",
      requester: "Requester",
      notes: "Visit notes",
      doctor: "Ordering doctor",
    },
  },

  // Results
  results: {
    noDataAvailable: "No Data Available",
    noDataAvailableSubtitle: "This sample has no results or visit information available.",
    edited: "edited",
    pending: {
      title: "Pending Results",
      rejectAll: "Reject All",
      acceptAll: "Accept All",
      reject: "Reject",
      accept: "Accept",
      addAndAccept: "Add & Accept",
      moreActions: "More actions",
      expand: "Expand All",
      collapse: "Collapse All",
      viewHistory: "View History",
      viewHistoryDescription: "View patient history for this test.",
      flagsDescription: "View flags associated with this result.",
      rejectDescription: "Reject this result",
      editDescription: "Edit this result",
      resultTimeTooltip: "Time when result was generated by instrument",

      acceptSuccess: "Result accepted successfully",
      acceptError: "Failed to accept result",
      acceptAllSuccess: "All results accepted",
      acceptAllError: "Failed to accept all results",
      rejectSuccess: "Result rejected",
      rejectError: "Failed to reject result",
      rejectAllSuccess: "All results rejected",
      rejectAllError: "Failed to reject all results",

      errors: {
        networkError: "Network error. Please check your connection and try again.",
        serverError: "Server error. Please contact support if this persists.",
        loadingFailed: "Failed to load data. Please refresh and try again.",
        pleaseTryAgain: "Please try again.",
        nonDeterministicProfile:
          "The result profile for this pending test is non-deterministic. Please contact an administrator.",
      },
    },
    current: {
      title: "Current Results",
      resultTimeTooltip: "Time when result was validated and published to LIS",
    },
    editedIndicator: "This result has been manually edited",
    editTooltip: {
      previousValue: "Previous value",
      editedBy: "Edited by",
      editedAt: "Edited at",
    },
    refRange: "Ref. Range",
    viewHistory: "View patient history",
    loadingData: "Loading data...",
  },

  flags: {
    descriptions: {
      0: "Unknown",
      1: "Significantly below reference range",
      2: "Below reference range",
      3: "Slightly below normal",
      4: "Within normal limits",
      5: "Slightly above normal",
      6: "Above reference range",
      7: "Critically high level",
    },
  },

  // Alerts
  alerts: {
    visitMissing: {
      title: "Visit Information Missing",
      message: "There is no visit information associated with this sample.",
    },
    noResults: {
      title: "No Results Found",
      message: "There are no results associated with this sample.",
    },
    flaggingMissing: {
      title: "Flagging Information Missing",
      message: "There is no flagging information associated with this sample.",
    },
  },

  // Dialogs
  dialogs: {
    // Edit Sample Dialog
    editSample: {
      title: "Edit Sample Barcode",
      description: "You're editing the barcode for sample: <strong>{barcode}</strong>",
      newBarcodeLabel: "New Barcode",
      hint: "Barcode can contain only letters, numbers, dash (-) and underscore (_)",
      errors: {
        required: "Barcode is required",
        barcodeExists: "A sample with this barcode already exists",
        containsQuotes: "Barcode cannot contain quotes (' or \")",
        containsBackslash: "Barcode cannot contain backslash (\\)",
        containsSpecialChars: "Barcode cannot contain special characters (newline, tab)",
        invalidCharacters: "Barcode can only contain letters, numbers, dash (-) and underscore (_)",
        networkError: "Network error - please try again",
      },
      success: "Sample barcode updated successfully",
      error: "Failed to update sample barcode",
    },

    // Edit Result Dialog
    editResult: {
      title: "Edit Result",
      fields: {
        testName: "Test Name",
        originalResult: "Original Result",
        newResult: "New Result",
        reason: "Reason for Edit (Optional)",
        reasonPlaceholder: "e.g., Corrected instrument error, Manual recalculation",
        units: "Units",
        range: "Reference Range",
        resultTime: "Date / Time",
      },
      preview: {
        title: "Parsed Value",
        parsed: "Will be saved as",
      },
      changePreview: "Change",
      validation: {
        required: "This field is required",
        invalidNumeric: "Please enter a valid numeric value",
        belowRange: "Below range: {range}",
        aboveRange: "Above range: {range}",
        withinRange: "Within range: {range}",
      },
      success: "Result updated successfully",
      error: "Failed to update result",
    },

    // Filter Samples Dialog
    filterSamples: {
      title: "Filter Samples",
      priority: {
        label: "Priority: {status}",
        stat: "high priority",
        all: "all",
      },
      fields: {
        locations: "Locations",
        instruments: "Instruments",
        sampleTypes: "Sample Types",
        visitTypes: "Visit Types",
        requesters: "Requesters",
        doctors: "Doctors",
      },
    },
    // Patient History Dialog
    patientHistory: {
      title: "Patient History - {testName}",
      subtitle: "{count} historical results",
      loading: "Loading patient history...",
      noHistory: "No historical results found for this test",
      range: "Range: {range}",

      // Error states
      errors: {
        serverTitle: "Something went wrong",
        serverSubtitle: "Failed to load history. Please try again.",
        networkTitle: "Connection lost",
        networkSubtitle: "Unable to reach the server. Check your connection.",
        timeoutTitle: "Request timed out",
        timeoutSubtitle: "The server is taking too long. Please try again.",
        authTitle: "Session expired",
        authSubtitle: "Please log in again to continue.",
        unknownTitle: "Unexpected error",
        unknownSubtitle: "Something went wrong. Please try again later.",
        validationTitle: "Invalid request",
        validationSubtitle: "The request could not be processed.",
        retry: "Try again",
        login: "Log in",
      },

      // Tabs
      tabs: {
        data: "Data",
        chart: "Chart",
      },

      // Table headers
      table: {
        visitId: "Visit ID",
        resultTime: "Date / Time",
        result: "Result",
        currentVisit: "Current visit",
      },

      // Chart labels
      chart: {
        results: "Results",
        normalRange: "Ref. Range",
        currentVisit: "Current Visit",
        current: "Current",
        date: "Date",
        value: "Units",
        tooltipResult: "Result",
        tooltipUnits: "Units",
        tooltipRefRange: "Ref. Range",
        tooltipCurrentVisit: "Current visit",
        noChartData: "No numeric data available for chart display",
      },
    },

    // Reject All Dialog
    rejectAll: {
      title: "Confirm Reject All",
      message:
        "You are about to reject <strong>all results</strong> for sample: <strong>{barcode}</strong>",
      warning: "All pending results associated with this sample will be permanently deleted.",
      confirm: "Reject All Results",
    },

    flags: {
      title: "Flags",
      headers: {
        level: "Level",
        source: "Source",
        code: "Code",
        description: "Description",
      },
      levels: {
        0: "Unknown",
        1: "Information",
        2: "Warning",
        3: "Error",
        4: "Critical",
      },
    },
  },

  signalr: {
    connected: "Real-time updates active",
    disconnected: "Real-time updates disconnected",
    reconnecting: "Reconnecting to real-time updates...",
    barcodeChanged: "Barcode changed: {old} → {new}",
    connectionFailed:
      "Failed to connect to SignalR after {attempts} attempts. Page will work, but without real-time updates.",
    resultsAdded: "{count} new results for {barcode}",
    resultsDeleted: "{count} deleted results for {barcode}",
    resultsModified: "{count} modified results for {barcode}",
    sampleAdded: "New sample with barcode: {barcode} added",
    sampleRemoved: "Sample {barcode} has been removed",
  },
};
